import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatCOP, StatusBadge } from "@/components/common";
import { Utensils, Calendar, FileText, Loader2 } from "lucide-react";

export default function ClientPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/clients/${token}`)
      .then(async r => {
        if (!r.ok) throw new Error("not found");
        return r.json();
      })
      .then(setData)
      .catch(() => setError("Portal no encontrado o el enlace expiró."))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-[#0B0D12] flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-[#F97316] animate-spin"/>
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen bg-[#0B0D12] flex items-center justify-center px-6">
      <div className="max-w-md text-center">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-red-500/10 flex items-center justify-center mb-4">
          <FileText className="w-6 h-6 text-red-400" strokeWidth={1.75}/>
        </div>
        <h1 className="text-xl font-bold text-[#F5F5F7] mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>Portal no disponible</h1>
        <p className="text-sm text-[#9CA3AF]">{error || "El enlace no es válido."}</p>
      </div>
    </div>
  );

  const { brand_name, client, upcoming_appointments, invoices, owner_name } = data;
  const totalPending = invoices.filter(i => i.status !== "cobrada").reduce((s,i)=>s+i.amount, 0);

  return (
    <div className="min-h-screen bg-[#0B0D12]">
      <header className="border-b border-[#262A33] bg-[#0B0D12]/80 backdrop-blur">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#F97316] flex items-center justify-center">
            <Utensils className="w-5 h-5 text-white" strokeWidth={2}/>
          </div>
          <div>
            <div className="font-bold text-[#F5F5F7] text-[15px]" style={{ fontFamily: "Manrope, sans-serif" }}>{brand_name}</div>
            <div className="text-[11px] text-[#9CA3AF]">Portal privado</div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="mb-8 flex items-center gap-5">
          {client.logo_url ? (
            <img src={client.logo_url} alt={client.name} className="w-20 h-20 rounded-2xl object-cover border border-[#262A33] hidden sm:block"/>
          ) : null}
          <div>
            <div className="text-sm text-[#9CA3AF] mb-1">Hola,</div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#F5F5F7] tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
              {client.contact_name || client.name}
            </h1>
            {client.contact_name && client.name && (
              <p className="text-sm text-[#F97316] mt-1 font-medium">{client.name}</p>
            )}
            <p className="text-sm text-[#9CA3AF] mt-2">
              Este es tu resumen con {brand_name}. Aquí podrás ver tus próximas reuniones y el estado de tus facturas.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-5">
            <div className="text-xs text-[#9CA3AF] mb-1">Servicio contratado</div>
            <div className="text-lg font-bold text-[#F5F5F7] capitalize" style={{ fontFamily: "Manrope, sans-serif" }}>{client.service}</div>
          </div>
          <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-5">
            <div className="text-xs text-[#9CA3AF] mb-1">
              {client.billing_type === "unico" ? "Valor del proyecto" : "Retainer mensual"}
            </div>
            <div className="text-lg font-bold text-[#F97316]" style={{ fontFamily: "Manrope, sans-serif" }}>{formatCOP(client.monthly_value)}</div>
          </div>
          <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-5">
            <div className="text-xs text-[#9CA3AF] mb-1">Pendiente de pago</div>
            <div className={`text-lg font-bold ${totalPending>0 ? "text-amber-400" : "text-emerald-400"}`} style={{ fontFamily: "Manrope, sans-serif" }}>{formatCOP(totalPending)}</div>
          </div>
        </div>

        <section className="bg-[#15171E] border border-[#262A33] rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-4 h-4 text-[#F97316]" strokeWidth={1.75}/>
            <h2 className="font-bold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>Próximas reuniones</h2>
          </div>
          {upcoming_appointments.length === 0 ? (
            <p className="text-sm text-[#9CA3AF]">No tienes reuniones agendadas por ahora.</p>
          ) : (
            <div className="space-y-2">
              {upcoming_appointments.map(a => {
                const d = new Date(a.scheduled_at);
                return (
                  <div key={a.appointment_id} className="flex items-center gap-4 p-3 rounded-xl bg-[#0B0D12] border border-[#262A33]" data-testid={`portal-appt-${a.appointment_id}`}>
                    <div className="text-center w-14">
                      <div className="text-[10px] text-[#F97316] font-semibold uppercase">{d.toLocaleString("es-CO",{month:"short"})}</div>
                      <div className="text-lg text-[#F5F5F7] font-bold leading-tight">{d.getDate()}</div>
                      <div className="text-[10px] text-[#9CA3AF]">{d.toLocaleTimeString("es-CO",{hour:"2-digit",minute:"2-digit"})}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#F5F5F7] font-medium">{a.title}</div>
                      <div className="text-xs text-[#9CA3AF] capitalize">{a.meeting_type} · {a.duration_min} min</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="bg-[#15171E] border border-[#262A33] rounded-2xl p-6 mb-10">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-4 h-4 text-[#F97316]" strokeWidth={1.75}/>
            <h2 className="font-bold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>Facturas</h2>
          </div>
          {invoices.length === 0 ? (
            <p className="text-sm text-[#9CA3AF]">Sin facturas emitidas todavía.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[#9CA3AF] border-b border-[#262A33]">
                    <th className="text-left font-medium py-2">Número</th>
                    <th className="text-left font-medium py-2">Concepto</th>
                    <th className="text-left font-medium py-2">Emitida</th>
                    <th className="text-left font-medium py-2">Vence</th>
                    <th className="text-left font-medium py-2">Estado</th>
                    <th className="text-right font-medium py-2">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(i => (
                    <tr key={i.invoice_id} className="border-b border-[#262A33] last:border-0">
                      <td className="py-2 text-[#F5F5F7] font-mono text-xs">{i.number}</td>
                      <td className="py-2 text-[#9CA3AF]">{i.concept}</td>
                      <td className="py-2 text-[#9CA3AF]">{i.issued_at}</td>
                      <td className="py-2 text-[#9CA3AF]">{i.due_at}</td>
                      <td className="py-2"><StatusBadge status={i.status}/></td>
                      <td className="py-2 text-right text-[#F5F5F7] font-medium">{formatCOP(i.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <p className="text-xs text-[#6B7280] text-center">
          Para cualquier consulta escribe a tu contacto en {brand_name}{owner_name ? `: ${owner_name}` : ""}.
        </p>
      </main>
    </div>
  );
}
